import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  LayoutDashboard, Receipt, Building2, Briefcase, Users,
  FileSpreadsheet, CreditCard, LogOut, BookOpen, Package, Award, CalendarDays, Landmark, UserCog, Activity, KeyRound, GraduationCap, Bell, MapPin, Store, FileText, Flame, Stethoscope,
} from "lucide-react";

const OWNER_NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/calendario", label: "Calendario", icon: CalendarDays },
  { to: "/ordini", label: "Ordini", icon: Package },
  { to: "/attestati", label: "Attestati", icon: Award },
  { to: "/vendite", label: "Vendite", icon: Receipt },
  { to: "/preventivi", label: "Incarichi", icon: Briefcase },
  { to: "/antincendio", label: "Antincendio", icon: Flame },
  { to: "/medico-competente", label: "Medico Competente", icon: Stethoscope },
  { to: "/aziende", label: "Aziende Clienti", icon: Building2 },
  { to: "/allievi", label: "Allievi / Clienti", icon: GraduationCap },
  { to: "/sedi-territoriali", label: "Sedi Territoriali", icon: MapPin },
  { to: "/fondi", label: "Fondi", icon: Landmark },
  { to: "/servizi", label: "Catalogo Servizi", icon: Briefcase },
  { to: "/partner", label: "Partner e Royalty", icon: Users },
  { to: "/rivenditori", label: "Da fatturare Rivenditori", icon: Store },
  { to: "/collaboratori", label: "Collaboratori", icon: UserCog },
  { to: "/attivita", label: "Attività Staff", icon: Activity },
  { to: "/notifiche", label: "Notifiche", icon: Bell },
  { to: "/sibill", label: "Import Sibill", icon: FileSpreadsheet },
  { to: "/pagamenti", label: "Stato Pagamenti", icon: CreditCard },
];

const COLLAB_NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/calendario", label: "Calendario", icon: CalendarDays },
  { to: "/ordini", label: "Ordini", icon: Package },
  { to: "/attestati", label: "Attestati", icon: Award },
  { to: "/vendite", label: "Vendite", icon: Receipt },
  { to: "/medico-competente", label: "Medico Competente", icon: Stethoscope },
  { to: "/aziende", label: "Aziende Clienti", icon: Building2 },
  { to: "/allievi", label: "Allievi / Clienti", icon: GraduationCap },
  { to: "/sedi-territoriali", label: "Sedi Territoriali", icon: MapPin },
  { to: "/fondi", label: "Fondi", icon: Landmark },
  { to: "/catalogo", label: "Catalogo e Ordini", icon: BookOpen },
  { to: "/notifiche", label: "Notifiche", icon: Bell },
];

const PARTNER_NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/calendario", label: "Calendario Corsi", icon: CalendarDays },
  { to: "/catalogo", label: "Catalogo e Ordini", icon: BookOpen },
  { to: "/ordini", label: "I Miei Ordini", icon: Package },
  { to: "/attestati", label: "I Miei Attestati", icon: Award },
  { to: "/allievi", label: "I Miei Allievi", icon: GraduationCap },
  { to: "/sedi-territoriali", label: "Sedi Territoriali", icon: MapPin },
  { to: "/fondi", label: "Fondi", icon: Landmark },
  { to: "/vendite", label: "Le Mie Vendite", icon: Receipt },
  { to: "/pagamenti", label: "Pagamenti", icon: CreditCard },
];

export default function Layout() {
  const { user, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const nav = user.role === "owner" ? OWNER_NAV : user.role === "collaboratore" ? COLLAB_NAV : PARTNER_NAV;
  const roleLabel = user.role === "owner" ? "Amministratore" : user.role === "collaboratore" ? "Collaboratore" : "Partner";
  const [badges, setBadges] = useState({ new_orders: 0, unread: 0, order_updates: 0 });
  const [notifs, setNotifs] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);

  const fetchBadges = () => api.get("/badges").then((r) => setBadges(r.data)).catch(() => {});
  const fetchNotifs = () => api.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {});
  useEffect(() => {
    fetchBadges();
    fetchNotifs();
    const id = setInterval(() => { fetchBadges(); fetchNotifs(); }, 20000);
    const onRefresh = () => { fetchBadges(); fetchNotifs(); };
    window.addEventListener("badges-refresh", onRefresh);
    return () => { clearInterval(id); window.removeEventListener("badges-refresh", onRefresh); };
  }, []);
  useEffect(() => { fetchBadges(); /* eslint-disable-next-line */ }, [location.pathname]);

  const unreadNotifs = notifs.filter((n) => !n.letto).length;
  const toggleNotifs = () => {
    const opening = !notifOpen;
    setNotifOpen(opening);
    if (opening && unreadNotifs > 0) {
      api.put("/notifications/read").then(() => { fetchNotifs(); fetchBadges(); }).catch(() => {});
    }
  };

  const badgeFor = (to) => {
    if (user.role === "partner") {
      if (to === "/attestati") return badges.unread;
      if (to === "/ordini") return badges.order_updates;
      return 0;
    }
    return to === "/ordini" ? badges.new_orders : 0;
  };

  const doLogout = () => { logout(); navigate("/login"); };

  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwdBusy, setPwdBusy] = useState(false);
  const emptyPwd = { current_password: "", new_password: "", confirm: "" };
  const [pwd, setPwd] = useState(emptyPwd);
  const changePassword = async () => {
    if (pwd.new_password.length < 8) return toast.error("La nuova password deve avere almeno 8 caratteri");
    if (pwd.new_password !== pwd.confirm) return toast.error("Le due password non coincidono");
    setPwdBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: pwd.current_password, new_password: pwd.new_password });
      toast.success("Password aggiornata con successo");
      setPwdOpen(false); setPwd(emptyPwd);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setPwdBusy(false); }
  };

  const isStaffUser = user.role === "owner" || user.role === "collaboratore";
  const [notifEmailOpen, setNotifEmailOpen] = useState(false);
  const [notifEmailBusy, setNotifEmailBusy] = useState(false);
  const [notifEmailData, setNotifEmailData] = useState({ login: "", email_notifiche: "", nome: "", notifiche_attive: true });
  const openNotifEmail = () => {
    api.get("/auth/me").then((r) => {
      setNotifEmailData({ login: r.data.email || "", email_notifiche: r.data.email_notifiche || "", nome: r.data.nome || "", notifiche_attive: r.data.notifiche_attive !== false });
      setNotifEmailOpen(true);
    }).catch(() => toast.error("Impossibile caricare i dati profilo"));
  };
  const saveNotifEmail = async () => {
    setNotifEmailBusy(true);
    try {
      if (user.role === "owner" && notifEmailData.nome.trim() && notifEmailData.nome.trim() !== user.nome) {
        const r = await api.put("/auth/display-name", { nome: notifEmailData.nome.trim() });
        setUser({ ...user, nome: r.data.nome });
      }
      const body = { email_notifiche: notifEmailData.email_notifiche.trim() };
      if (user.role === "partner") body.notifiche_attive = notifEmailData.notifiche_attive !== false;
      await api.put("/auth/notification-email", body);
      toast.success("Profilo aggiornato");
      setNotifEmailOpen(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setNotifEmailBusy(false); }
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <aside className="w-64 shrink-0 bg-dusty-900 text-slate-200 flex flex-col fixed inset-y-0 left-0 z-30">
        <div className="px-5 py-6 border-b border-white/10">
          <div className="flex items-center gap-2.5">
            <div className="h-10 w-10 rounded-lg bg-white flex items-center justify-center overflow-hidden shrink-0">
              <img src="/logo-safe.png" alt="Safe Consulting srl" className="h-8 w-8 object-contain" />
            </div>
            <div>
              <div className="font-display font-bold text-white leading-tight">Safe Consulting srl</div>
              <div className="text-[10px] uppercase tracking-widest text-dusty-300">Gestionale</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.to === "/" ? "dashboard" : n.to.slice(1)}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive ? "bg-dusty-500 text-white shadow-sm" : "text-slate-300 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <n.icon className="h-[18px] w-[18px]" />
              <span className="flex-1">{n.label}</span>
              {badgeFor(n.to) > 0 && (
                <span data-testid={`nav-badge-${n.to.slice(1)}`} className="min-w-[20px] h-5 px-1.5 inline-flex items-center justify-center rounded-full bg-rose-500 text-white text-[11px] font-bold">
                  {badgeFor(n.to)}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <div className="px-2 pb-2">
            <div className="text-sm font-medium text-white truncate">{user.nome}</div>
            <div className="text-xs text-dusty-300">{roleLabel}</div>
          </div>
          <button
            onClick={() => { setPwd(emptyPwd); setPwdOpen(true); }}
            data-testid="change-password-btn"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-300 hover:bg-white/5 hover:text-white transition-colors"
          >
            <KeyRound className="h-4 w-4" /> Cambia password
          </button>
          <button
            onClick={openNotifEmail}
            data-testid="notif-email-btn"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-300 hover:bg-white/5 hover:text-white transition-colors"
          >
            <Bell className="h-4 w-4" /> Profilo e notifiche
          </button>
          <button
            onClick={doLogout}
            data-testid="logout-btn"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-300 hover:bg-white/5 hover:text-white transition-colors"
          >
            <LogOut className="h-4 w-4" /> Esci
          </button>
        </div>
      </aside>

      <Dialog open={pwdOpen} onOpenChange={(v) => { setPwdOpen(v); if (!v) setPwd(emptyPwd); }}>
        <DialogContent className="bg-white max-w-md" data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Cambia password</DialogTitle>
            <DialogDescription>Inserisci la password attuale e scegline una nuova (almeno 8 caratteri).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div><Label>Password attuale</Label><Input type="password" value={pwd.current_password} onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })} className="mt-1.5" data-testid="pwd-current" autoComplete="current-password" /></div>
            <div><Label>Nuova password</Label><Input type="password" value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })} className="mt-1.5" data-testid="pwd-new" autoComplete="new-password" /></div>
            <div><Label>Conferma nuova password</Label><Input type="password" value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} className="mt-1.5" data-testid="pwd-confirm" autoComplete="new-password" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPwdOpen(false); setPwd(emptyPwd); }}>Annulla</Button>
            <Button onClick={changePassword} disabled={pwdBusy} data-testid="pwd-submit" className="bg-dusty-700 hover:bg-dusty-800 text-white">{pwdBusy ? "Salvo…" : "Aggiorna password"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={notifEmailOpen} onOpenChange={(v) => setNotifEmailOpen(v)}>
        <DialogContent className="bg-white max-w-md" data-testid="notif-email-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Profilo</DialogTitle>
            <DialogDescription>Gestisci il tuo nome visualizzato e l'indirizzo su cui ricevere le notifiche (nuovi ordini, iscrizioni, ecc.).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            {user.role === "owner" && (
              <div>
                <Label>Nome visualizzato</Label>
                <Input value={notifEmailData.nome} onChange={(e) => setNotifEmailData({ ...notifEmailData, nome: e.target.value })} placeholder="Il tuo nome" className="mt-1.5" data-testid="profile-nome-input" />
                <p className="text-xs text-slate-400 mt-1.5">Il nome mostrato nell'app (es. nel menu e negli ordini in carico).</p>
              </div>
            )}
            <div>
              <Label>Email di accesso</Label>
              <Input value={notifEmailData.login} disabled className="mt-1.5 bg-slate-50 text-slate-500" data-testid="notif-email-login" />
            </div>
            <div>
              <Label>Email di ricezione notifiche</Label>
              <Input type="email" value={notifEmailData.email_notifiche} onChange={(e) => setNotifEmailData({ ...notifEmailData, email_notifiche: e.target.value })} placeholder="Lascia vuoto per usare l'email di accesso" className="mt-1.5" data-testid="notif-email-input" disabled={user.role === "partner" && notifEmailData.notifiche_attive === false} />
              <p className="text-xs text-slate-400 mt-1.5">Se lasci vuoto, le notifiche arriveranno all'email di accesso.</p>
            </div>
            {user.role === "partner" && (
              <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700 pt-1">
                <input type="checkbox" checked={notifEmailData.notifiche_attive !== false} onChange={(e) => setNotifEmailData({ ...notifEmailData, notifiche_attive: e.target.checked })} data-testid="notif-attive-input" className="h-4 w-4 accent-dusty-600" />
                Ricevi le notifiche via email {notifEmailData.notifiche_attive === false && "(disattivate)"}
              </label>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNotifEmailOpen(false)}>Annulla</Button>
            <Button onClick={saveNotifEmail} disabled={notifEmailBusy} data-testid="notif-email-submit" className="bg-dusty-700 hover:bg-dusty-800 text-white">{notifEmailBusy ? "Salvo…" : "Salva"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="flex-1 ml-64 flex flex-col">
        <header className="sticky top-0 z-20 backdrop-blur-md bg-white/85 border-b border-slate-200 px-8 py-3.5 flex items-center justify-between">
          <div className="text-sm text-slate-500">Gestionale Consulting &amp; Sicurezza</div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <button onClick={toggleNotifs} data-testid="notif-bell"
                className="relative h-9 w-9 inline-flex items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 transition-colors">
                <Bell className="h-5 w-5" />
                {unreadNotifs > 0 && (
                  <span data-testid="notif-unread-count" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center rounded-full bg-rose-500 text-white text-[10px] font-bold">
                    {unreadNotifs}
                  </span>
                )}
              </button>
              {notifOpen && (
                <>
                  <div className="fixed inset-0 z-30" onClick={() => setNotifOpen(false)} />
                  <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg z-40" data-testid="notif-panel">
                    <div className="px-4 py-2.5 border-b border-slate-100 text-sm font-semibold text-slate-700">Notifiche</div>
                    {notifs.length === 0 ? (
                      <div className="px-4 py-8 text-center text-sm text-slate-400" data-testid="notif-empty">Nessuna notifica</div>
                    ) : (
                      notifs.map((n) => (
                        <div key={n.id} className={`px-4 py-3 border-b border-slate-50 text-sm ${n.letto ? "text-slate-500" : "text-slate-800 bg-dusty-50/40"}`} data-testid={`notif-item-${n.id}`}>
                          <div className="leading-snug">{n.messaggio}</div>
                          <div className="text-[11px] text-slate-400 mt-1">{new Date(n.created_at).toLocaleString("it-IT")}</div>
                        </div>
                      ))
                    )}
                    {user.role !== "partner" && (
                      <button onClick={() => { setNotifOpen(false); navigate("/notifiche"); }}
                        className="w-full text-center py-2.5 text-sm font-medium text-dusty-700 hover:bg-slate-50 border-t border-slate-100" data-testid="notif-see-all">
                        Vedi tutte le notifiche
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${
              user.role === "owner" ? "bg-dusty-50 text-dusty-700 border-dusty-200" : user.role === "collaboratore" ? "bg-indigo-50 text-indigo-700 border-indigo-200" : "bg-amber-50 text-amber-700 border-amber-200"
            }`} data-testid="role-badge">
              {roleLabel}
            </span>
            <span className="text-xs text-slate-400">IT 🇮🇹</span>
          </div>
        </header>
        <main className="flex-1 p-6 sm:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
